import React, { useCallback, useMemo } from "react"
import { Box, Button, Flex, Heading, Text } from "@chakra-ui/react"
import type { ProductVariant } from "@shopify/hydrogen-react/storefront-api-types"

import { useCart } from "@app/hooks/useCart"
import { useAppContext } from "@app/providers/app"
import { useShopifyPrice } from "@app/hooks/useShopify"
import { useCore } from "@app/hooks/useCore"
import { useOutOfStock } from "@app/hooks/useOutOfStock"
import "@app/theme/css/product.css"
import { useGlobalContext } from "@app/providers/global"

type Props = {
  handleBackInStock: () => void
  handleFindInStore?: () => void
  isUnavailable: boolean
  loading?: boolean
  product: GatsbyTypes.SanityProduct
  quantity: number
  selectedVariant: ProductVariant | null
  productSizeText: string
}

const ProductAddToCartBottom: React.FC<Props> = ({
  handleBackInStock,
  handleFindInStore,
  product,
  quantity,
  loading,
  isUnavailable,
  selectedVariant,
  productSizeText,
}) => {
  const { addToCart, loading: cartLoading } = useCart()
  const { activeVariant } = useAppContext()
  const { additionalStockOut, additionalAddToCartProduct, additionalAddToCartLoading, additionalInStoreOnly } = useGlobalContext()

  const { formattedPrice, formattedCompareAtPrice, onSale } = useShopifyPrice(activeVariant, quantity)
  const {
    helpers: { decodeShopifyId },
  } = useCore()

  const shopifyVariant = useMemo(
    () =>
      product.variants.find((v: any) => {
        return decodeShopifyId(v.id, "ProductVariant") === decodeShopifyId(activeVariant?.id, "ProductVariant")
      }),
    [product.variants, activeVariant?.id, decodeShopifyId]
  )

  const available = useMemo(
    () => product?.published && shopifyVariant?.availableForSale && isUnavailable === false,
    [shopifyVariant?.availableForSale, product?.published, isUnavailable]
  )
  const { stockStatus, isNotifyFlow, isInStoreOnly } = useOutOfStock(product)

  const showAvailable = useMemo(() => available || !activeVariant || loading, [activeVariant, available, loading])

  const text = useMemo(() => {
    if (product?.inStoreOnly) {
      return additionalInStoreOnly
    } else {
      return cartLoading ? additionalAddToCartLoading : additionalAddToCartProduct
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartLoading, product?.inStoreOnly])

  const onClick = useCallback(async () => {
    if (available && activeVariant && !loading && !cartLoading) {
      await addToCart(product, activeVariant, quantity)
      if (handleFindInStore) handleFindInStore()
    }
  }, [available, activeVariant, loading, cartLoading, quantity, product, addToCart, handleFindInStore])

  return (
    <Box
      zIndex={700}
      p="24px"
      position="relative"
      borderTop="5px solid"
      borderColor="primary.navy"
      background="primary.white"
      display={{ base: "block", md: "none" }}
    >
      <Flex flexDir="column" gap="8px" px={{ base: 0, sm: "32px" }}>
        <Flex gap={4} justifyContent="space-between">
          <Heading variant="heading24" fontWeight={500}>
            {product.title}
          </Heading>
          <Heading variant="heading24" fontWeight={500}>
            {formattedPrice}
          </Heading>
        </Flex>
        <Text lineHeight={1.5}>{product.description}</Text>
        <Text lineHeight={1.5}>{productSizeText}</Text>
      </Flex>
      <Button
        variant="fillRainbowLoader"
        w="100%"
        alignItems="center"
        border="none"
        mt="40px"
        isDisabled={
          (!isNotifyFlow && selectedVariant && !selectedVariant?.availableForSale) || isInStoreOnly || cartLoading || product?.inStoreOnly
        }
        onClick={() => (isNotifyFlow ? handleBackInStock() : selectedVariant?.availableForSale && showAvailable && onClick())}
        transition="none"
        aria-label={stockStatus || text}
        textAlign="center"
        title={stockStatus || text}
        {...(!selectedVariant
          ? {
              _hover: { backgroundColor: "brand.darkBlue", color: "primary.white" },
              _active: { backgroundColor: "brand.darkBlue", color: "primary.white" },
            }
          : {})}
      >
        {stockStatus ? (
          stockStatus
        ) : selectedVariant && !selectedVariant?.availableForSale ? (
          additionalStockOut
        ) : (
          <>
            <Text variant="text18">{stockStatus || text}</Text>
            <Flex ml="10px" display="inline-flex">
              {onSale && (
                <Text variant="text18" pr="5px" textDecoration="line-through">
                  {formattedCompareAtPrice}
                </Text>
              )}
              <Text variant="text18">{formattedPrice}</Text>
            </Flex>
          </>
        )}
      </Button>
    </Box>
  )
}

export default React.memo(ProductAddToCartBottom)
